import type { AxiosResponse, Canceler } from 'axios';
import axios, { isCancel } from 'axios';
import * as React from 'react';
import { usePrevious } from '../Utils/usePrevious';
import { checkEquals } from '../Utils/utils';
import { useDebounce } from '~/Utils/Hooks/useDebounce';
import { axiosWrapper } from './axiosWrapper';
import type { FetchDataAction, FetchDataHookProps, FetchDataHookReturn, FetchDataHookState } from './types';

function reducer<TResponseType>(
    state: FetchDataHookState<TResponseType>,
    action: FetchDataAction<TResponseType>
): FetchDataHookState<TResponseType> {
    switch (action.type) {
        case 'SET_DATA':
            return {
                ...state,
                data: action.payload,
                loading: false,
                error: undefined,
            };
        case 'SET_ERROR':
            return {
                ...state,
                loading: false,
                data: undefined,
                error: action.payload,
            };
        case 'START_REQUEST':
            return {
                ...state,
                error: undefined,
                loading: true,
                dataRequested: true,
            };
        default:
            return state;
    }
}

export function useFetchData<TResponseType, TParamsType extends object = object, TRequestDataType = object>(
    props: FetchDataHookProps<TParamsType, TRequestDataType>
): FetchDataHookReturn<TResponseType> {
    const { url, params, method = 'get', shouldMakeRequest = true, debounceTime = 200, timeout = 0 } = props;

    const [{ data, loading, error, dataRequested }, dispatch] = React.useReducer<
        React.Reducer<FetchDataHookState<TResponseType>, FetchDataAction<TResponseType>>
    >(reducer, {
        data: undefined,
        loading: false,
        error: undefined,
        dataRequested: false,
    });

    const previousProps = usePrevious({ url, method, params });
    const requestWithDebounce = useDebounce(request, debounceTime);

    const cancelToken = React.useRef<Canceler>();

    React.useEffect(() => {
        if (shouldMakeRequest) {
            fetchData();
        }

        return () => {
            if (cancelToken.current) {
                cancelToken.current();
            }
        };
    }, []);

    React.useEffect(() => {
        const propsChanged = previousProps
            ? previousProps.url !== url || previousProps.method !== method || !checkEquals(previousProps.params, params)
            : false;

        if ((propsChanged || !dataRequested) && shouldMakeRequest) {
            fetchData();
        }
    }, [url, method, params, dataRequested, shouldMakeRequest]);

    function fetchData() {
        if (cancelToken.current) {
            cancelToken.current();
        }

        dispatch({ type: 'START_REQUEST' });
        requestWithDebounce();
    }

    function request() {
        axiosWrapper
            .request<TParamsType, AxiosResponse<TResponseType>>({
                url,
                method,
                params,
                timeout,
                cancelToken: new axios.CancelToken(token => {
                    cancelToken.current = token;
                }),
            })
            .then(response => {
                cancelToken.current = undefined;
                dispatch({ type: 'SET_DATA', payload: response.data });
            })
            .catch(e => {
                if (isCancel(e)) {
                    return;
                }

                dispatch({ type: 'SET_ERROR', payload: e.message });
                console.error(e);
            });
    }

    return {
        data,
        loading,
        error,
    };
}
